import React from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import { fg } from '@atlaskit/platform-feature-flags';
import type { Box as PlatformBox } from '@atlaskit/primitives';
import { Box as NativeBox } from './native-box';
import { Box as PrimitivesBox } from './__generated__/index.partial';
import type { XCSSProp } from '../../utils/xcssValidator';

type PlatformBoxProps = React.ComponentProps<typeof PlatformBox>;

export type BoxProps = Pick<PlatformBoxProps, 'children' | 'ref' | 'testId'> & {
	/**
	 * A token alias for background color. See: [Design tokens](https://atlassian.design/components/tokens/all-tokens)
	 * for a list of available colors.
	 *
	 * When the background color is set to a surface token, the current surface CSS variable
	 * will also be set to this value in the `Box` styles.
	 *
	 * @type [Background color tokens](https://atlassian.design/components/tokens/all-tokens#color-background)
	 */
	backgroundColor?: PlatformBoxProps['backgroundColor'];

	/**
	 * @type ForgeComponent
	 */
	children?: PlatformBoxProps['children'];

	/**
	 * A shorthand for `paddingBlock` and `paddingInline` together.
	 *
	 * @type [Space tokens](https://atlassian.design/components/tokens/all-tokens#space)
	 */
	padding?: PlatformBoxProps['padding'];

	/**
	 * The logical block start and end padding of an element.
	 *
	 * @type [Space tokens](https://atlassian.design/components/tokens/all-tokens#space)
	 */
	paddingBlock?: PlatformBoxProps['paddingBlock'];

	/**
	 * The logical block end padding of an element.
	 *
	 * @type [Space tokens](https://atlassian.design/components/tokens/all-tokens#space)
	 */
	paddingBlockEnd?: PlatformBoxProps['paddingBlockEnd'];

	/**
	 * The logical block start padding of an element.
	 *
	 * @type [Space tokens](https://atlassian.design/components/tokens/all-tokens#space)
	 */
	paddingBlockStart?: PlatformBoxProps['paddingBlockStart'];

	/**
	 * The logical inline start and end padding of an element.
	 *
	 * @type [Space tokens](https://atlassian.design/components/tokens/all-tokens#space)
	 */
	paddingInline?: PlatformBoxProps['paddingInline'];

	/**
	 * The logical inline end padding of an element.
	 *
	 * @type [Space tokens](https://atlassian.design/components/tokens/all-tokens#space)
	 */
	paddingInlineEnd?: PlatformBoxProps['paddingInlineEnd'];

	/**
	 * The logical inline start padding of an element.
	 *
	 * @type [Space tokens](https://atlassian.design/components/tokens/all-tokens#space)
	 */
	paddingInlineStart?: PlatformBoxProps['paddingInlineStart'];

	/**
	 * Accessible role.
	 *
	 * @type string
	 */
	role?: PlatformBoxProps['role'];

	/**
	 * Apply a subset of permitted styles, powered by Atlassian Design System tokens.
	 * For a list of supported style properties on this component, see [here](/platform/forge/ui-kit/components/xcss).
	 *
	 * @type XCSSProp
	 */
	xcss?: XCSSProp;
};

/**
 * A box is a generic container that provides managed access to design tokens.
 */
export const Box = (props: Parameters<RenderFn>[0]): React.JSX.Element => {
	if (fg('platform_migrate_to_native_box')) {
		return <NativeBox {...props} />;
	}
	return <PrimitivesBox {...props} />;
};
